import React from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { myCandidatesActions } from './MyCandidates.redux';
import {
  selectMyCandidatesQuickviewMetrics,
  selectMyCandidatesSelectedQuickviewKey,
} from './MyCandidates.selector';

const useStyles = makeStyles()(theme => ({
  root: {
    minWidth: 236,
    maxWidth: 260,
    padding: theme.spacing(2),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: 'calc(100vh - 11rem)',
    maxHeight: 'calc(100vh - 11rem)',
    boxSizing: 'border-box',
  },
  tabs: {
    minHeight: 36,
    flexShrink: 0,
    marginBottom: theme.spacing(2),
    '& .MuiTab-root': { minHeight: 36 },
  },
  metricsScroll: {
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: 1,
    minHeight: 0,
    paddingBottom: theme.spacing(2),
  },
  metricItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child': { borderBottom: 'none' },
  },
  badge: {
    minWidth: 32,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    fontSize: 14,
    fontWeight: 600,
    color: 'white',
    flexShrink: 0,
  },
  badgeBlue: { backgroundColor: '#006FB9' },
  badgeOrange: { backgroundColor: '#E65100' },
  badgeGreen: { backgroundColor: '#2E7D32' },
  badgeRed: { backgroundColor: '#C62828' },
  metricLabel: {
    fontSize: 13,
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
}));

const getBadgeClass = (color: string) => {
  switch (color) {
    case 'blue': return 'badgeBlue';
    case 'orange': return 'badgeOrange';
    case 'green': return 'badgeGreen';
    case 'red': return 'badgeRed';
    default: return 'badgeBlue';
  }
};

export const QuickviewPanel = () => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const metrics = useSelector(selectMyCandidatesQuickviewMetrics);
  const selectedKey = useSelector(selectMyCandidatesSelectedQuickviewKey);
  const [tabValue, setTabValue] = React.useState(0);

  const handleMetricClick = (key: string) => {
    dispatch(myCandidatesActions.setSelectedQuickviewKey(selectedKey === key ? null : key));
  };

  const isPriorityTab = tabValue === 0;

  return (
    <Box className={classes.root}>
      <Tabs
        value={tabValue}
        onChange={(_, v: number) => setTabValue(v)}
        className={classes.tabs}
        variant="fullWidth">
        <Tab label="PRIORITY" sx={{ textTransform: 'uppercase', fontWeight: 600 }} />
        <Tab label="SUGGESTED" sx={{ textTransform: 'uppercase' }} />
        <Tab label="MINE" sx={{ textTransform: 'uppercase' }} />
      </Tabs>
      <Box className={classes.metricsScroll}>
        {isPriorityTab && metrics.map(m => (
          <Box
            key={m.key}
            className={classes.metricItem}
            onClick={() => handleMetricClick(m.key)}
            sx={selectedKey === m.key ? { backgroundColor: 'rgba(0, 0, 0, 0.08)' } : undefined}>
            <Box className={cx(classes.badge, classes[getBadgeClass(m.color)])}>{m.count}</Box>
            <Typography className={classes.metricLabel} variant="body2">
              {m.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
